import { useEffect, useState } from "react";
import http from "../../utilities/HttpModule";

// Module-level cache to store blob URLs and avoid refetching
const imageCache = new Map<string, string>();

// Track ongoing fetches to prevent duplicate requests
const pendingFetches = new Map<string, Promise<string>>();

/**
 * Hook to fetch images from authenticated endpoints.
 * Fetches with JWT Authorization header and converts to blob URL for rendering.
 * Includes caching to prevent duplicate fetches for the same URL.
 */
const useAuthenticatedImage = (imageUrl: string | null | undefined) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!imageUrl) {
      // blobUrl cannot be derived: it is resolved asynchronously and read out
      // of a module-level cache that render must not touch. This synchronous
      // reset (and the cache-hit path below) are branches of the same fetch
      // effect; splitting them out into derived state would change when a
      // stale blob URL stops being rendered.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
      setBlobUrl(null);
      return;
    }

    // Check cache first
    const cached = imageCache.get(imageUrl);
    if (cached) {
      setBlobUrl(cached);
      return;
    }

    // Check if there's already a pending fetch for this URL
    const pending = pendingFetches.get(imageUrl);
    if (pending) {
      pending.then((url) => setBlobUrl(url)).catch(() => setError(true));
      return;
    }

    const fetchImage = async (): Promise<string> => {
      const response = await http.get(imageUrl, {
        responseType: "blob",
      });

      const url = URL.createObjectURL(response.data);
      imageCache.set(imageUrl, url);
      return url;
    };

    setLoading(true);
    setError(false);

    const fetchPromise = fetchImage();
    pendingFetches.set(imageUrl, fetchPromise);

    fetchPromise
      .then((url) => {
        setBlobUrl(url);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
        pendingFetches.delete(imageUrl);
      });
  }, [imageUrl]);

  return { blobUrl, loading, error };
};

/**
 * Clears a specific URL from the cache (useful after avatar upload).
 */
export const clearImageCache = (imageUrl: string) => {
  const cached = imageCache.get(imageUrl);
  if (cached) {
    URL.revokeObjectURL(cached);
    imageCache.delete(imageUrl);
  }
};

/**
 * Clears all cached images.
 */
export const clearAllImageCache = () => {
  imageCache.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
  imageCache.clear();
};

export default useAuthenticatedImage;
